import { useMemo } from 'react';
import CommandIcon from './CommandIcon.jsx';
import { featureMeasurement, normalizeSymbol, symbolUrl } from '../lib/mapPresentation.js';
import { Layers3, MapPin, Radio, UsersRound } from 'lucide-react';
import {
  featureId, featureLabel, teamColor, teamFeatures, teamTrackers, trackerColor, trackerPosition,
  visibleFeatures, visibleTeams, visibleTrackers
} from '../lib/session.js';

export default function SessionPanel({ snapshot, side, activeTab, onTabChange, selected, onSelect, onCollapse, language, t }) {
  const session = snapshot.session || {};
  const features = visibleFeatures(session);
  const teams = visibleTeams(session);
  const trackers = visibleTrackers(snapshot);
  const teamData = useMemo(() => new Map(visibleTeams(session).map((team) => [team.id, {
    members: teamTrackers(team, snapshot), areas: teamFeatures(team, session)
  }])), [session, snapshot]);
  const teamsByFeature = useMemo(() => {
    const result = new Map();
    for (const team of teams) for (const feature of teamData.get(team.id).areas) {
      const assigned = result.get(feature) || [];
      assigned.push(team);
      result.set(feature, assigned);
    }
    return result;
  }, [teamData]);
  const tab = side === 'features' ? 'features' : activeTab;
  const selectedFeature = selected?.type === 'feature'
    ? features.find((feature, index) => featureId(feature, index) === selected.id) : null;
  const selectedTracker = selected?.type === 'tracker'
    ? trackers.find((tracker) => String(tracker.id) === selected.id) : null;
  const tabs = [
    { id: 'features', icon: Layers3, count: features.length },
    { id: 'teams', icon: UsersRound, count: teams.length },
    { id: 'trackers', icon: Radio, count: trackers.length }
  ];
  return (
    <aside className={`info-panel info-panel--${side}`} data-testid={`${side}-panel`} aria-label={t(`viewer.${side === 'features' ? 'features' : 'teams'}`)}>
      <div className="panel-heading">
        {side === 'features' ? <Layers3 aria-hidden="true" /> : <UsersRound aria-hidden="true" />}
        <h2>{t(`viewer.${side === 'features' ? 'features' : 'teams'}`)}</h2>
        <span className="panel-count">{side === 'features' ? features.length : teams.length}</span>
        <button type="button" className="icon-button panel-collapse" onClick={onCollapse} aria-label={t('viewer.hidePanel')}><CommandIcon name={side === 'features' ? 'caret-left' : 'caret-right'} /></button>
      </div>
      {side !== 'features' && (
        <div className="panel-tabs" role="tablist" aria-label={t('viewer.inspect')}>
          {tabs.map(({ id, icon: Icon, count }) => (
            <button key={id} type="button" role="tab" id={`tab-${id}`} aria-controls="session-items"
              aria-selected={tab === id} className={`${tab === id ? 'is-active' : ''} tab-${id}`}
              onClick={() => onTabChange(id)} data-testid={`${id}-tab`}>
              {t(`viewer.${id}`)}<span>{count}</span>
            </button>
          ))}
        </div>
      )}
      <div className="panel-list" id={side === 'features' ? undefined : 'session-items'}
        role={side === 'features' ? undefined : 'tabpanel'} aria-labelledby={side === 'features' ? undefined : `tab-${tab}`}>
        {tab === 'features' && (features.length ? features.map((feature, index) => {
          const id = featureId(feature, index);
          const assignedTeams = teamsByFeature.get(feature) || [];
          return (
            <button key={id} type="button" data-item-id={id}
              className={`info-card info-card--feature${selected?.type === 'feature' && selected.id === id ? ' is-selected' : ''}`}
              onClick={() => onSelect({ type: 'feature', id })}>
              <span className="drawing-card-copy info-card__copy">
                <strong>{featureLabel(feature, index)}</strong>
                <span>{featureMeasurement(feature, language) || String(feature.properties?.kind || feature.geometry.type)}</span>
                {assignedTeams.length > 0 && <small>{assignedTeams.map((team) => team.name).join(' · ')}</small>}
              </span>
              <span className="drawing-card-tools">
                {normalizeSymbol(feature.properties?.poiIcon) ? <img className="drawing-symbol" alt="" src={symbolUrl(feature.properties.poiIcon)} /> : <i className="drawing-color-swatch" style={{ background: feature.properties?.color || '#1565C0' }} />}
                <span className="drawing-kind"><CommandIcon name={feature.geometry.type === 'Point' ? 'map-pin' : feature.geometry.type === 'Polygon' ? 'polygon' : 'path'} /></span>
                <span className="drawing-focus"><CommandIcon name="crosshair" /></span>
              </span>
            </button>
          );
        }) : <p className="empty-message">{t('viewer.emptyFeatures')}</p>)}
        {tab === 'teams' && (teams.length ? teams.map((team) => {
          const { members, areas } = teamData.get(team.id);
          const located = members.filter(trackerPosition).length;
          return (
            <section key={team.id} className={`team-card${selected?.type === 'team' && selected.id === String(team.id) ? ' is-selected' : ''}`} data-team-id={team.id}>
              <button type="button" className="team-card-header" onClick={() => onSelect({ type: 'team', id: String(team.id) })}>
                <span className="info-card__copy"><strong className={`team-title${areas.length ? ' is-area-linked' : ''}`} style={{ '--team-color': teamColor(team, snapshot) }}>{team.name}</strong>
                  <span>{t('viewer.teamPositions', { count: located, total: members.length })}</span>
                </span>
                <span className="drawing-focus team-focus-icon"><CommandIcon name="crosshair" /></span>
              </button>
              {areas.length > 0 && <div className="team-areas">{areas.map((feature, index) => (
                <button key={featureId(feature, index)} type="button" style={{ '--area-color': feature.properties?.color || '#64b5f6' }} onClick={() => onSelect({ type: 'feature', id: featureId(feature, index) })}>
                  <Layers3 aria-hidden="true" />{featureLabel(feature, index)}
                </button>
              ))}</div>}
              <div className="team-members">{members.map((tracker) => (
                <button key={tracker.id} type="button" style={{ '--tracker-color': trackerColor(tracker) }} onClick={() => onSelect({ type: 'tracker', id: String(tracker.id) })}>
                  <CommandIcon name={tracker.deviceType === 'tractive' ? 'paw-print' : tracker.deviceType === 'lora' ? 'radio' : 'device-mobile'} />
                  <span>{tracker.label || tracker.id}</span><small>{trackerPosition(tracker) ? `ID ${tracker.id}` : t('viewer.noPosition')}</small>
                  <span className="team-member-focus"><CommandIcon name="crosshair" /></span>
                </button>
              ))}</div>
              {!located && <p className="team-position-note">{areas.length ? t('viewer.teamAtArea') : t('viewer.noPosition')}</p>}
            </section>
          );
        }) : <p className="empty-message">{t('viewer.emptyTeams')}</p>)}
        {tab === 'trackers' && (trackers.length ? trackers.map((tracker) => (
          <button key={tracker.id} type="button" data-item-id={tracker.id} style={{ '--tracker-color': trackerColor(tracker) }}
            className={`info-card info-card--tracker${selected?.type === 'tracker' && selected.id === String(tracker.id) ? ' is-selected' : ''}`}
            onClick={() => onSelect({ type: 'tracker', id: String(tracker.id) })}>
            <span className="tracker-device-icon" style={{ '--tracker-color': trackerColor(tracker) }}><CommandIcon name={tracker.deviceType === 'tractive' ? 'paw-print' : tracker.deviceType === 'lora' ? 'radio' : 'device-mobile'} /></span>
            <span className="info-card__copy"><strong>{tracker.label || tracker.id}</strong>
              <span>{trackerPosition(tracker) ? t('viewer.lastPosition') : t('viewer.noPosition')}</span>
              {tracker.teamNames?.length > 0 && <small>{tracker.teamNames.join(' · ')}</small>}
            </span>
            {tracker.battery != null && Number.isFinite(Number(tracker.battery)) && <span className="tracker-metrics">{Math.round(Number(tracker.battery))}%</span>}
          </button>
        )) : <p className="empty-message">{t('viewer.emptyTrackers')}</p>)}
      </div>
      {tab === 'features' && selectedFeature && <div className="selection-details" data-testid="feature-details">
        <strong>{featureLabel(selectedFeature)}</strong>
        {selectedFeature.properties?.description && <p>{String(selectedFeature.properties.description)}</p>}
        <span>{selectedFeature.geometry.type}</span>
      </div>}
      {tab === 'trackers' && selectedTracker && <div className="selection-details" data-testid="tracker-details">
        <strong>{selectedTracker.label || selectedTracker.id}</strong>
        <span>{trackerPosition(selectedTracker)?.map((coordinate) => coordinate.toFixed(5)).join(', ') || t('viewer.noPosition')}</span>
        <span>{t('viewer.updated', { time: selectedTracker.updatedAt ? new Date(selectedTracker.updatedAt).toLocaleTimeString() : '—' })}</span>
      </div>}
    </aside>
  );
}
